import React, { useState, useEffect, useMemo } from 'react'

import { useVenti } from 'venti'
import {
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Warning } from '@mui/icons-material'
import Page from '../../../components/Page'
import { useAppContext } from '../../../components/AppContext'
import { useAppContextActions } from '../../../components/AppContext/AppHooks'
import { getTheme } from '../../../config'
import { Roles } from '../../../services/client'
import {
	formatDate,
	getErrorMessage,
	getMixpanelUserProperties,
	formattedUserRoles,
} from '../../../services/helper'
import userQuestions from '../../../workflows/user-questions'
import { useAlert } from '../../../hooks'
import { ModalRemoveRestoreRecordConfirm } from '../../../components/modals/ModalRemoveRestoreRecordConfirm'
import { ModalUser } from '../../../components/modals/ModalUser'
import { ModalChangePassword } from '../../../components/modals/ModalChangePassword'
import { useMixpanel } from '../../../hooks/useMixpanel'
import { eventTypes } from '../../../services/constants'
import queryKeys from '../../../services/queryKeys'
import PaginatedDataTable from '../../../components/PaginatedDataTable'
import useSearchText from '../../../hooks/useSearchText'
import { navigationLinking } from '../../../services/navigation'
import { TheBigPOSApi } from '../../../lib/TheBigPOSClient'
import { formatPhoneWithCountryCode } from '../../../services/formattingUtils'
import {
	DEFAULT_PAGE_NUMBER,
	DEFAULT_PAGE_SIZE,
	DEFAULT_SORT_BY,
	DEFAULT_SORT_DIRECTION,
} from '../../../constants/pagination'

const theme = getTheme()

export default function AdminUsersView() {
	const { formatMessage } = useIntl()
	const { state } = useAppContext()
	const { alert } = useAlert()
	const { applyImpersonationRequest, applyUser } =
		useAppContextActions()
	const navigate = useNavigate()
	const { user, siteConfig } = state
	const ventiState = useVenti()
	const queryClient = useQueryClient()

	const [modalChangePassword, setModalChangePassword] = useState({
		visible: false,
		data: null,
	})
	const [removeModalVisible, setRemoveModalVisible] = useState(false)
	const [restoreModalVisible, setRestoreModalVisible] =
		useState(false)
	const [activeRow, setActiveRow] = useState()
	const mixpanel = useMixpanel()
	const [filters, setFilters] = useState({
		role: 'all',
		active: 'active',
	})
	const [modalAddNewUser, setModalAddNewUser] = useState({
		visible: false,
		data: {},
		title: 'Create Admin User',
	})
	const [paginationParams, setPaginationParams] = useState({
		pageSize: DEFAULT_PAGE_SIZE,
		pageNumber: DEFAULT_PAGE_NUMBER,
		sortBy: DEFAULT_SORT_BY,
		sortDirection: DEFAULT_SORT_DIRECTION,
	})

	const {
		isFetching,
		isRefetching,
		isError,
		data: usersResult,
		refetch,
	} = useQuery({
		queryKey: [queryKeys.users],
		queryFn: () => {
			const filterPayload = {
				searchText,
				isActive:
					filters?.active === 'all'
						? null
						: filters?.active === 'active',
				roles: filters?.role === 'all' ? null : [filters?.role],
			}

			if (user.role !== Roles.superAdmin) {
				filterPayload.excludeRoles = [Roles.superAdmin]
			}
			return TheBigPOSApi.searchUsers(filterPayload, paginationParams)
		},
		retry: 2,
	})

	const filteredUsers = useMemo(() => {
		if (user.role === Roles.superAdmin) {
			return usersResult?.data.rows || []
		}
		return (
			usersResult?.data.rows.filter(
				(row) => row.role !== Roles.superAdmin
			) || []
		)
	}, [usersResult, user.role])

	const {
		searchText,
		onChange: onSearchChange,
		onClear: onSearchClear,
	} = useSearchText({ onFetch: refetch })

	const handlePaginationChange = (params) => {
		setPaginationParams(params)
	}
	const totalCount = useMemo(() => {
		return usersResult?.data ? usersResult.data.count : 0
	}, [usersResult])

	const handleFilterChange = (e, field) => {
		setFilters((f) => ({ ...f, [field]: e.target.value }))
	}

	const roleOptions = Object.values(Roles)
		.sort()
		.filter(
			(role) =>
				user.role === Roles.superAdmin || role !== Roles.superAdmin
		)

	const columns = [
		{
			name: 'Email',
			selector: (row) => row.email,
			sortable: true,
			sortField: 'email',
		},
		{
			name: 'First Name',
			selector: (row) => row.firstName,
			sortable: true,
			sortField: 'firstName',
		},
		{
			name: 'Last Name',
			selector: (row) => row.lastName,
			sortable: true,
			sortField: 'lastName',
		},
		{
			name: 'Role',
			selector: (row) => formattedUserRoles.get(row.role),
			sortable: true,
			sortField: 'role',
		},
		{
			name: 'Created',
			selector: (row) => formatDate(row.createdAt, false),
			sortable: true,
			hide: theme.breakpoints.dataTable,
			sortField: 'createdAt',
		},
	]

	useEffect(() => {
		refetch()
	}, [paginationParams, filters, refetch])

	useEffect(() => {
		if (isError) {
			alert('There was a problem loading the users', {
				severity: 'error',
			})
		}
	}, [isError, alert])

	const handleSaveUser = async (data) => {
		const result = await createUserMutation.mutateAsync({
			...data,
			siteConfigurationId: siteConfig.id,
			userRole: Roles.admin,
			phone: formatPhoneWithCountryCode(data.phone),
		})
		mixpanel.trackEvent(
			eventTypes.USER_CREATED,
			getMixpanelUserProperties(result.data)
		)
		return result.data
	}

	const showRemoveModal = (row) => {
		setActiveRow({ ...row, name: `${row.firstName} ${row.lastName}` })
		setRemoveModalVisible(true)
	}

	const showChangePWModal = (data) => {
		setModalChangePassword({
			visible: true,
			data,
		})
	}

	const createUserMutation = useMutation({
		mutationFn: (data) => TheBigPOSApi.createUser(data),
		onSuccess: async () => {
			await queryClient.invalidateQueries([queryKeys.users])
		},
	})

	const removeMutation = useMutation({
		mutationFn: (id) => TheBigPOSApi.deleteUser(id),
		onSuccess: async () => {
			await queryClient.invalidateQueries([queryKeys.users])

			mixpanel.trackEvent(
				eventTypes.USER_DELETED,
				getMixpanelUserProperties(activeRow)
			)
			setRemoveModalVisible(false)
			alert(
				`User "${activeRow.firstName} ${activeRow.lastName}" successfully removed`
			)
			setActiveRow(null)
		},
		onError: (e) => {
			alert(getErrorMessage(e), { severity: 'error' })
			ventiState.set(
				theme.storageKeys.errorMessage,
				e?.data?.message || theme.api_messages.server_error
			)
		},
	})

	const restoreMutation = useMutation({
		mutationFn: (id) => TheBigPOSApi.restoreUser(id),
		onSuccess: async () => {
			await queryClient.invalidateQueries([queryKeys.users])

			mixpanel.trackEvent(
				eventTypes.USER_RESTORED,
				getMixpanelUserProperties(activeRow)
			)

			setRestoreModalVisible(false)
			alert(
				`User "${activeRow.firstName} ${activeRow.lastName}" successfully restored`
			)
			setActiveRow(null)
		},
		onError: (e) => {
			alert(getErrorMessage(e), { severity: 'error' })
			ventiState.set(
				theme.storageKeys.errorMessage,
				e?.data?.message || theme.api_messages.server_error
			)
		},
	})

	const nukeMutation = useMutation({
		mutationFn: (id) =>
			TheBigPOSApi.deleteUser(id, {
				permanent: true,
			}),
		onSuccess: async () => {
			await queryClient.invalidateQueries([queryKeys.users])

			mixpanel.trackEvent(
				eventTypes.USER_NUKED,
				getMixpanelUserProperties(activeRow)
			)
			setRemoveModalVisible(false)
			alert(
				`User "${activeRow.firstName} ${activeRow.lastName} (${activeRow.email})" was permanently deleted`
			)
			setActiveRow(null)
		},
		onError: (e) => {
			alert(getErrorMessage(e), { severity: 'error' })
			ventiState.set(
				theme.storageKeys.errorMessage,
				e?.data?.message || theme.api_messages.server_error
			)
		},
	})

	const restoreUser = async () => {
		if (activeRow && activeRow?.id)
			await restoreMutation.mutateAsync(activeRow?.id)
	}

	const removeUser = async () => {
		if (activeRow && activeRow?.id)
			if (!activeRow?.nuke)
				await removeMutation.mutateAsync(activeRow.id)
			else await nukeMutation.mutateAsync(activeRow.id)
	}

	const showRestoreModal = (row) => {
		setRestoreModalVisible(true)
		setActiveRow({ ...row, name: `${row.firstName} ${row.lastName}` })
	}

	const onRowClicked = () => {
		// todo: user edit with proper functionality
	}

	const handleForceImpersonation = async (row) => {
		try {
			await TheBigPOSApi.forceImpersonation({ email: row.email })

			mixpanel.trackEvent(eventTypes.IMPERSONATION_FORCED, {
				impersonator: getMixpanelUserProperties(user),
				impersonatee: getMixpanelUserProperties(row),
			})

			applyImpersonationRequest({
				status: 'Active',
				impersonator: {
					firstName: user.firstName,
					lastName: user.lastName,
					email: user.email,
				},
				impersonatee: {
					firstName: row.firstName,
					lastName: row.lastName,
					email: row.email,
				},
			})

			applyUser({
				...user,
				role: row.role || 'Borrower',
			})

			navigate(`/${navigationLinking.Portal}`)
		} catch (e) {}
	}

	const handleImpersonationRequest = async (row) => {
		try {
			await TheBigPOSApi.requestImpersonation({
				email: row.email,
			})
			alert(
				`Impersonation request was sent to ${row.firstName} ${row.lastName} (${row.email})`
			)

			mixpanel.trackEvent(eventTypes.IMPERSONATION_REQUESTED, {
				impersonator: getMixpanelUserProperties(user),
				impersonatee: getMixpanelUserProperties(row),
			})

			applyImpersonationRequest({
				status: 'Requested',
				impersonator: {
					firstName: user.firstName,
					lastName: user.lastName,
					email: user.email,
				},
				impersonatee: {
					firstName: row.firstName,
					lastName: row.lastName,
					email: row.email,
				},
			})
		} catch (e) {
			alert(getErrorMessage(e), { severity: 'error' })
			ventiState.set(
				theme.storageKeys.errorMessage,
				e?.data?.message || theme.api_messages.server_error
			)
		}
	}

	const handleAddNewUser = () => {
		setModalAddNewUser((current) => ({ ...current, visible: true }))
	}

	const actionItems = [
		{
			name: 'Change Password',
			onClick: (e, row) => showChangePWModal(row),
			hideIf: (row) => row.deletedAt !== null,
		},
		{
			name: 'Delete',
			onClick: (e, row) => showRemoveModal(row),
			hideIf: (row) => row.deletedAt !== null,
		},
		{
			name: 'Restore',
			onClick: (e, row) => showRestoreModal(row),
			hideIf: (row) => row.deletedAt === null,
		},
	]

	if (user.canImpersonate || user.role === Roles.superAdmin)
		actionItems.push({
			name: 'Impersonate',
			onClick: (e, row) =>
				user.role === Roles.superAdmin
					? handleForceImpersonation(row)
					: handleImpersonationRequest(row),
			hideIf: (row) => row.deletedAt !== null,
		})

	if (user.role === Roles.superAdmin)
		actionItems.push({
			name: (
				<p>
					Nuke User <Warning />
				</p>
			),
			onClick: (e, row) => showRemoveModal({ ...row, nuke: true }),
			hideIf: null,
		})

	return (
		<Page title="Users" isFullWidth>
			<div className="pl-5 pr-5 pb-10 h-full overflow-auto">
				<ModalChangePassword
					modal={modalChangePassword.visible}
					setModal={setModalChangePassword}
					modalData={modalChangePassword.data}
					modalTitle="Change Password"
					user={user}
				/>
				<ModalUser
					modal={modalAddNewUser}
					setModal={setModalAddNewUser}
					questions={userQuestions}
					save={handleSaveUser}
				/>
				<ModalRemoveRestoreRecordConfirm
					removeModalVisible={removeModalVisible}
					setRemoveModalVisible={setRemoveModalVisible}
					remove={removeUser}
					row={activeRow}
					loading={removeMutation.isPending}
				/>
				<ModalRemoveRestoreRecordConfirm
					removeModalVisible={restoreModalVisible}
					setRemoveModalVisible={setRestoreModalVisible}
					restore={restoreUser}
					row={activeRow}
					loading={restoreMutation.isPending}
				/>
				<PaginatedDataTable
					data={filteredUsers}
					columns={columns}
					defaultSortAsc={DEFAULT_SORT_DIRECTION === 'asc'}
					defaultSortFieldId={DEFAULT_SORT_BY}
					paginationTotalRows={totalCount}
					onPaginationChange={handlePaginationChange}
					progressPending={isFetching && !isRefetching}
					title="Users"
					onRefreshClick={refetch}
					keyField="id"
					onSearchChange={onSearchChange}
					onClearSearchClick={onSearchClear}
					searchText={searchText}
					refreshing={isRefetching}
					searchFilters={
						<>
							<FormControl id="filterType">
								<InputLabel>Role</InputLabel>
								<Select
									value={filters.role}
									onChange={(e) => handleFilterChange(e, 'role')}
									label={formatMessage({ id: 'role' })}
								>
									<MenuItem value="all">All</MenuItem>
									{roleOptions.map((role) => (
										<MenuItem key={role} value={role}>
											{formattedUserRoles.get(role)}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl id="filterActive">
								<InputLabel>
									{formatMessage({ id: 'global.active_deleted' })}
								</InputLabel>
								<Select
									value={filters.active}
									onChange={(e) => handleFilterChange(e, 'active')}
									label={formatMessage({
										id: 'global.active_deleted',
									})}
								>
									<MenuItem value="active">Show Active Only</MenuItem>
									<MenuItem value="deleted">
										Show Deleted Only
									</MenuItem>
									<MenuItem value="all">Show All</MenuItem>
								</Select>
							</FormControl>
						</>
					}
					exportEnabled
					fixedHeader
					actionItems={actionItems}
					addNewBtn={{
						text: 'Add Admin User',
						onClick: handleAddNewUser,
					}}
				/>
			</div>
		</Page>
	)
}
